// frontend/src/services/websocket.js
import io from 'socket.io-client';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.subscribers = new Map();
    this.currentSymbol = null;
    this.currentInterval = null;
  }

  connect(symbol, onUpdate, options = {}) {
const WS_URL = process.env.REACT_APP_WS_URL || 'http://localhost:5000';
const DEBUG = process.env.REACT_APP_DEBUG === 'true';
    const interval = options.interval || '1m';
    this.currentSymbol = symbol;
    this.currentInterval = interval;
    
    // Disconnect existing connection
    if (this.socket) {
      this.disconnect();
    }

    // Create new connection
    this.socket = io(WS_URL, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    // Connection events
    this.socket.on('connect', () => {
      if (DEBUG) {
        console.log('WebSocket connected');
      }
      this.subscribe(symbol, interval);
    });

    this.socket.on('disconnect', () => {
      if (DEBUG) {
        console.log('WebSocket disconnected');
      }
    });

    this.socket.on('error', (error) => {
      console.error('WebSocket error:', error);
    });

    // Data events
    this.socket.on('price_update', (data) => {
      if (onUpdate) {
        onUpdate({ type: 'price_update', ...data });
      }
      this.notifySubscribers('price_update', data);
    });

    this.socket.on('indicator_update', (data) => {
      if (onUpdate) {
        onUpdate({ type: 'indicator_update', ...data });
      }
      this.notifySubscribers('indicator_update', data);
    });

    this.socket.on('new_signal', (data) => {
      if (onUpdate) {
        onUpdate({ type: 'new_signal', ...data });
      }
      this.notifySubscribers('new_signal', data);
    });

    this.socket.on('market_snapshot', (data) => {
      if (onUpdate) {
        onUpdate({ type: 'market_snapshot', ...data });
      }
      this.notifySubscribers('market_snapshot', data);
    });

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.currentSymbol = null;
    this.currentInterval = null;
    this.subscribers.clear();
  }

  subscribe(symbol, interval = this.currentInterval || '1m') {
    if (this.socket && this.socket.connected) {
      this.socket.emit('subscribe', { symbol, interval });
    }
  }

  unsubscribe(symbol) {
    if (this.socket && this.socket.connected) {
      this.socket.emit('unsubscribe', { symbol });
    }
  }

  // Subscribe to specific event types
  addSubscriber(eventType, callback) {
    if (!this.subscribers.has(eventType)) {
      this.subscribers.set(eventType, new Set());
    }
    this.subscribers.get(eventType).add(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.subscribers.get(eventType);
      if (callbacks) {
        callbacks.delete(callback);
      }
    };
  }

  notifySubscribers(eventType, data) {
    const callbacks = this.subscribers.get(eventType);
    if (callbacks) {
      callbacks.forEach(callback => callback(data));
    }
  }

  // Send custom events
  emit(event, data) {
    if (this.socket && this.socket.connected) {
      this.socket.emit(event, data);
    }
  }

  // Request manual update
  requestUpdate(symbol, updateType = 'all') {
    this.emit('request_update', { symbol, type: updateType });
  }

  // Keep connection alive
  ping() {
    this.emit('ping');
  }
}

// Create singleton instance
const wsService = new WebSocketService();

// Export convenience functions
export const connectWebSocket = (symbol, onUpdate, options) => wsService.connect(symbol, onUpdate, options);
export const disconnectWebSocket = () => wsService.disconnect();
export const subscribeToSymbol = (symbol, interval) => wsService.subscribe(symbol, interval);
export const unsubscribeFromSymbol = (symbol) => wsService.unsubscribe(symbol);
export const addWebSocketSubscriber = (eventType, callback) => wsService.addSubscriber(eventType, callback);
export const requestUpdate = (symbol, updateType) => wsService.requestUpdate(symbol, updateType);

export default wsService;
